import { useMemo } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import { getTypeaheadDatesAndOccupancy } from 'selectors/typeaheadSelectors'
import {
  mapFlexibleNightsToDurationRange,
  convertDateRangesToMonthString,
} from 'lib/search/searchUtils'

interface UseExtractDatesAndOccupancyResult {
  extractedDates: App.ExtractDatesResult | undefined
  extractedOccupancy: App.ExtractOccupancyResult | undefined
}

export const useExtractDatesAndOccupancy = (
  typeaheadId: string | undefined,
): UseExtractDatesAndOccupancyResult => {
  const { travelDates, occupancy } = useAppSelector((state) =>
    typeaheadId
      ? getTypeaheadDatesAndOccupancy(state, typeaheadId)
      : { travelDates: undefined, occupancy: undefined },
  )

  const extractedDates = useMemo((): App.ExtractDatesResult | undefined => {
    if (!travelDates) return undefined

    if (travelDates.type === 'specific') {
      return {
        checkinDate: travelDates.checkIn,
        checkoutDate: travelDates.checkOut,
        flexibleMonths: undefined,
        flexibleNights: undefined,
      }
    }

    if (travelDates.type === 'flexible') {
      return {
        checkinDate: undefined,
        checkoutDate: undefined,
        flexibleNights: mapFlexibleNightsToDurationRange(travelDates.numberOfNights),
        flexibleMonths: convertDateRangesToMonthString(travelDates.dateRanges),
      }
    }

    return undefined
  }, [travelDates])

  const extractedOccupancy = useMemo((): App.ExtractOccupancyResult | undefined => {
    if (!occupancy || occupancy.length === 0) return undefined

    return {
      occupancies: occupancy.map(room => ({
        adults: room.adults,
        children: room.childrenAges.length,
        childrenAge: room.childrenAges,
      })),
    }
  }, [occupancy])

  return {
    extractedDates,
    extractedOccupancy,
  }
}
